import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type DragEvent as ReactDragEvent, type ReactNode, type Ref } from "react";

import { CASES_KEY, conflictEntryId, ProblemError, RECORD_KEY, uploadCorrespondence } from "@/api";
import { Button } from "@/components/Button";
import buttons from "@/components/Button.module.css";
import { CheckIcon, CloseIcon, FileIcon } from "@/components/icons";
import { MeshMark } from "@/components/MeshMark";
import fields from "@/components/fields/Field.module.css";
import { Select } from "@/components/fields/Select";

import { dropSummary, type DropOutcome } from "./dropSummary";
import styles from "./Record.module.css";

/** Design §6.4 — the courtesy guard; the server refuses the same types again (422). */
const ACCEPT = ".eml,.msg,.pdf,.jpg,.jpeg,.png,.heic,.docx,.xlsx,.txt";
const SUFFIXES = ACCEPT.split(",");
/** The intake limits (design §6.3, 413): 25 MB per file, 20 files per drop. */
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 20;
const TYPE_REFUSED = "Dateityp nicht erlaubt — Mail (.eml, .msg), PDF, Bild, Word, Excel oder Text.";
/** Said before anything is dropped (UX ruling 7, F-1) — the steel area on the start page does the opposite. */
export const TRUST_LINE = "Wird nur in der Akte abgelegt — nichts davon geht an die Auslese.";
/** Over the column while a drop is still being filed (record fix wave, T8). */
export const STILL_FILING = "Die Ablage läuft noch — gleich wieder frei.";
/** Over the column of a cancelled Vorgang, which takes no file (spec 2026-10-15 §5). */
export const CANCELLED_DROP = "Der Vorgang ist storniert — hier wird nichts abgelegt.";
/** The id of the progress line — the reason the intake's buttons are held meanwhile. */
const PROGRESS_ID = "intake-progress";

/** What a drop mid-filing did — nothing, said so on screen and aloud. */
function busyRefusal(files: File[]): string {
  const names = files.map((file) => file.name).join(", ");
  return files.length === 1
    ? `Nicht abgelegt: ${names} — die Ablage lief noch. Zieh die Datei noch einmal hierher.`
    : `Nicht abgelegt: ${names} — die Ablage lief noch. Zieh die Dateien noch einmal hierher.`;
}

type Direction = "auto" | "email_in" | "email_out";

const DIRECTIONS: readonly { value: Direction; label: string }[] = [
  { value: "auto", label: "Automatisch" },
  { value: "email_in", label: "Eingang" },
  { value: "email_out", label: "Ausgang" },
];

function isAccepted(file: File): boolean {
  const name = file.name.toLowerCase();
  return SUFFIXES.some((suffix) => name.endsWith(suffix));
}

export interface Progress {
  /** 1-based: the file being filed now. */
  position: number;
  total: number;
  current: string;
}

/**
 * One file of a drop while it is being filed (UX gate round 2, #5): a
 * placeholder row where the entries appear, one per file, until the record
 * read again draws the entries themselves.
 */
export interface QueuedFile {
  key: string;
  name: string;
  state: "waiting" | "filing" | "filed" | "refused";
}

/**
 * Filing files into the record (design §8.2, D-2) — the state behind the
 * intake's tools and the drop target, kept at the record column so a drop on
 * any tab lands in the same place.
 *
 * **Not `components/DropArea`** (contract V-6): that one creates a Vorgang,
 * stores the drawing and queues a reading with the worker (paket-a) — the
 * one process that holds a model key (E-54). Exactly what a customer's mail
 * must never reach (§4.3). This only files, and says so (`TRUST_LINE`).
 *
 * Each file is its own request (plan D-4), in order, so one duplicate (409)
 * does not take the others down with it.
 */
export function useFileIntake(
  caseId: string,
  onAnnounce: (sentence: string) => void,
  onFiled: (entryIds: string[]) => void,
) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [queue, setQueue] = useState<QueuedFile[] | null>(null);
  // Which drop the queue belongs to: a second drop may start while the first
  // one's placeholders still wait for the record — the first must not clear them.
  const run = useRef(0);
  const [outcomes, setOutcomes] = useState<DropOutcome[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [direction, setDirection] = useState<Direction>("auto");

  // A file let go anywhere else on this page would make the *browser* open
  // it and throw the app away mid-Vorgang (the same guard as the start page).
  useEffect(() => {
    const over = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
    };
    const drop = (event: DragEvent) => event.preventDefault();
    document.addEventListener("dragover", over);
    document.addEventListener("drop", drop);
    return () => {
      document.removeEventListener("dragover", over);
      document.removeEventListener("drop", drop);
    };
  }, []);

  /**
   * A drop that arrives while the last one is still being filed (record fix
   * wave, T8): not queued behind it and not dropped silently — refused, and
   * the refusal stands under the intake until the next drop.
   */
  function refuseWhileBusy(files: File[]) {
    if (files.length === 0) return;
    const sentence = busyRefusal(files);
    setNotice(sentence);
    onAnnounce(sentence);
  }

  async function place(files: File[]) {
    if (files.length === 0) return;
    if (busy) {
      refuseWhileBusy(files);
      return;
    }
    setOutcomes(null);
    if (files.length > MAX_FILES) {
      const refused = `Höchstens ${MAX_FILES} Dateien auf einmal — bitte aufteilen.`;
      setNotice(refused);
      onAnnounce(refused);
      return;
    }
    setNotice(null);
    setBusy(true);
    run.current += 1;
    const mine = run.current;
    const mark = (index: number, state: QueuedFile["state"]) =>
      setQueue((previous) => previous?.map((each, at) => (at === index ? { ...each, state } : each)) ?? null);
    setQueue(files.map((file, index) => ({ key: `${mine}-${index}`, name: file.name, state: "waiting" })));
    const done: DropOutcome[] = [];
    for (const [index, file] of files.entries()) {
      setProgress({ position: index + 1, total: files.length, current: file.name });
      mark(index, "filing");
      if (!isAccepted(file)) {
        done.push({ file: file.name, ok: false, status: 422, detail: TYPE_REFUSED });
        mark(index, "refused");
        continue;
      }
      if (file.size > MAX_BYTES) {
        done.push({ file: file.name, ok: false, status: 413, detail: "größer als 25 MB." });
        mark(index, "refused");
        continue;
      }
      try {
        const entries = await uploadCorrespondence(caseId, [file], direction === "auto" ? undefined : direction);
        done.push({ file: file.name, ok: true, entries });
        mark(index, "filed");
      } catch (cause) {
        done.push({
          file: file.name,
          ok: false,
          status: cause instanceof ProblemError ? cause.status : 0,
          detail: cause instanceof ProblemError ? cause.userMessage : "Server nicht erreichbar.",
          existingEntryId: conflictEntryId(cause),
        });
        mark(index, "refused");
      }
    }
    setProgress(null);
    setBusy(false);
    setOutcomes(done);
    onAnnounce(dropSummary(done));
    onFiled(done.flatMap((outcome) => (outcome.ok ? outcome.entries.map((entry) => entry.id) : [])));
    // The whole `["cases"]` prefix: the register and the start page both show the last activity (M-5).
    void client.invalidateQueries({ queryKey: CASES_KEY });
    // The placeholders stand until the record read again draws the entries
    // in their place — cleared before, the column would close up and open
    // again a moment later. Whether or not the read succeeds.
    try {
      await client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
    } finally {
      if (run.current === mine) setQueue(null);
    }
  }

  function clear() {
    setOutcomes(null);
    setNotice(null);
  }

  return { busy, progress, queue, outcomes, notice, direction, setDirection, place, refuseWhileBusy, clear };
}

export type FileIntake = ReturnType<typeof useFileIntake>;

/**
 * Where a result line's „Zum Eintrag" leads: the entry already in the record
 * for a duplicate (Task 8); in a batch (screen check S-2) also the entry a
 * file just became — a single new entry is scrolled to by itself instead.
 */
function jumpTarget(outcome: DropOutcome, batch: boolean): string | null {
  if (outcome.ok) return batch ? (outcome.entries[0]?.id ?? null) : null;
  return outcome.status === 409 && outcome.existingEntryId ? outcome.existingEntryId : null;
}

/** One line per file (UX ruling 7): the word first, the colour only with it. */
function outcomeWord(outcome: DropOutcome): { word: string; tone: "ok" | "known" | "refused" } {
  if (outcome.ok) {
    const unreadable = outcome.entries.some((entry) => entry.kind === "file" && entry.parseNote !== null);
    return { word: unreadable ? "als Datei abgelegt (nicht lesbar)" : "abgelegt", tone: "ok" };
  }
  if (outcome.status === 409) return { word: "schon in der Akte", tone: "known" };
  return { word: `nicht abgelegt — ${outcome.detail}`, tone: "refused" };
}

/** The trust line's own id — „Ablegen …" is described by it while nothing is being filed. */
const TRUST_ID = "intake-trust";

/**
 * The intake as a toolbar in the record panel's head (Task 9, UX-Gutachten 1,
 * B-1): „Ablegen …" opens the file picker, „Notiz" the note form, and the
 * direction is a small select. A dashed row of its own under the tabs (UX
 * ruling 7, F-1) cost 112 px between the head and the first entry; the
 * whole panel is the drop target anyway (`DropTarget`), and that is where a
 * file is dragged to.
 *
 * What filing does not do (`TRUST_LINE`, F-1) stands in the toolbar as a
 * quiet line of its own and describes „Ablegen …"; the overlay says it again
 * the moment a file hovers. The empty record does not repeat it under its
 * mesh (Task 12): two lines apart, it was said twice. Held while a drop
 * is filed (record fix wave, T8): reachable, `aria-disabled`, with the
 * progress as the reason — `disabled` dropped the focus of whoever had just
 * picked the files. The select keeps its value meanwhile.
 */
export function IntakeTools({
  intake,
  onWriteNote,
  noteOpenerRef,
}: {
  intake: FileIntake;
  onWriteNote: () => void;
  /** „Notiz" — where the focus goes back when the note form closes. */
  noteOpenerRef?: Ref<HTMLButtonElement>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const held = intake.busy ? "true" : undefined;
  const heldBecause = intake.busy ? PROGRESS_ID : undefined;
  return (
    <div role="group" aria-label="Ablage" className={styles.tools}>
      <Button onClick={() => inputRef.current?.click()} aria-disabled={held} aria-describedby={heldBecause ?? TRUST_ID}>
        Ablegen …
      </Button>
      <Button
        ref={noteOpenerRef}
        variant="quiet"
        aria-disabled={held}
        aria-describedby={heldBecause}
        onClick={onWriteNote}
      >
        Notiz
      </Button>
      {/* Word and select stay together where the row wraps. */}
      <span className={styles.toolField}>
        <label className={`label ${styles.toolLabel}`} htmlFor="intake-direction">
          Richtung
        </label>
        <Select
          id="intake-direction"
          className={`${fields.input} ${styles.toolSelect}`}
          value={intake.direction}
          aria-disabled={held}
          aria-describedby={heldBecause}
          onChange={(event) => {
            if (!intake.busy) intake.setDirection(event.target.value as Direction);
          }}
        >
          {DIRECTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </span>
      {/* What filing does not do, readable before anything is picked or
          dropped (UX ruling 7, F-1; ruling of fix round 1): a quiet line of
          its own beside the tools, below them where the row wraps. */}
      <p id={TRUST_ID} className={styles.toolNote}>
        {TRUST_LINE}
      </p>
      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          void intake.place(files);
        }}
      />
    </div>
  );
}

/**
 * What the intake has to say in the panel's body, under its head — and only
 * when it has something: how far a drop has got, file by file, a refusal, and
 * what each file came to (UX gate round 2, #5). On an empty record it is the
 * empty state, with the grey mesh and what filing does not do. Nothing at all
 * otherwise: the entries start right under the filters.
 */
export function IntakeStatus({
  intake,
  empty,
  onJumpTo,
}: {
  intake: FileIntake;
  empty: boolean;
  onJumpTo: (entryId: string) => void;
}) {
  // More than one new entry: none of them is scrolled to (S-2), each gets its way here.
  const batch =
    (intake.outcomes ?? []).reduce((count, outcome) => count + (outcome.ok ? outcome.entries.length : 0), 0) > 1;
  const results = intake.outcomes !== null && intake.outcomes.length > 0;
  if (!empty && intake.progress === null && intake.notice === null && !results) return null;

  return (
    <div className={`${styles.intake} ${empty ? styles.intakeEmpty : ""}`}>
      {empty ? <MeshMark /> : null}
      {intake.progress !== null ? (
        <p className={`${styles.intakeText} ${styles.intakeFiling}`}>
          <span id={PROGRESS_ID}>
            Legt ab …{" "}
            <span className={styles.progressCount}>
              <span className="tabular">{intake.progress.position}</span> von{" "}
              <span className="tabular">{intake.progress.total}</span>
            </span>{" "}
            · {intake.progress.current}
          </span>
        </p>
      ) : empty ? (
        <p className={styles.intakeText}>Die Akte ist leer. Zieh Mails und Dateien hierher oder schreib eine Notiz.</p>
      ) : null}
      {intake.progress !== null && intake.queue !== null ? <FilingProgress queue={intake.queue} /> : null}
      {intake.notice !== null ? <p className={styles.intakeRefusal}>{intake.notice}</p> : null}
      {results ? (
        <div className={styles.results}>
          <ul className={styles.resultList} aria-label="Ergebnis der Ablage">
            {intake.outcomes!.map((outcome, index) => {
              const { word, tone } = outcomeWord(outcome);
              const Icon = tone === "ok" ? CheckIcon : tone === "known" ? FileIcon : CloseIcon;
              const target = jumpTarget(outcome, batch);
              return (
                <li key={`${index}-${outcome.file}`} className={styles.result} data-tone={tone}>
                  <Icon size={14} />
                  <span className={styles.resultFile}>{outcome.file}</span>
                  <span className={styles.resultWord}>{word}</span>
                  {target !== null ? (
                    <Button variant="quiet" flush onClick={() => onJumpTo(target)}>
                      Zum Eintrag
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <Button variant="quiet" flush onClick={intake.clear}>
            Schließen
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * How far a drop has got, file by file (UX gate round 2, #5): one segment
 * per file — filled once it is through, outlined while it goes. A count of
 * files, not a percentage of anything (CI §6): „1 von 3" in words beside it.
 */
function FilingProgress({ queue }: { queue: readonly QueuedFile[] }) {
  const through = queue.filter((each) => each.state === "filed" || each.state === "refused").length;
  return (
    <span
      className={styles.progress}
      role="progressbar"
      aria-label="Ablage"
      aria-valuemin={0}
      aria-valuemax={queue.length}
      aria-valuenow={through}
      aria-valuetext={`${through} von ${queue.length} Dateien durch`}
    >
      {queue.map((each) => (
        <span key={each.key} className={styles.progressStep} data-state={each.state} />
      ))}
    </span>
  );
}

/** The word a placeholder row says for its file. */
const QUEUE_WORD: Record<QueuedFile["state"], string> = {
  waiting: "wartet",
  filing: "wird abgelegt …",
  filed: "abgelegt",
  refused: "nicht abgelegt",
};

/**
 * The placeholder rows (UX gate round 2, #5): where the new entries will
 * stand, one row per file while the drop is filed — the file's name and
 * what is happening to it. Gone once the record read again draws the
 * entries; what each file came to stands under the intake as before.
 */
export function FilingRows({ queue }: { queue: readonly QueuedFile[] }) {
  return (
    <ol className={styles.filing} aria-label="Wird abgelegt">
      {queue.map((each) => (
        <li key={each.key} className={`${styles.entry} ${styles.filingRow}`} data-state={each.state}>
          <span className={styles.icon} aria-hidden="true">
            <FileIcon size={14} />
          </span>
          <p className={styles.filingLine}>
            <span className={styles.filingName}>{each.name}</span>
            <span className={styles.filingWord}> · {QUEUE_WORD[each.state]}</span>
          </p>
        </li>
      ))}
    </ol>
  );
}

/**
 * The intake's toolbar as the record's skeleton draws it (record fix round 2,
 * M-3): the same words in the same boxes — invisible, with the bones lying
 * on them — so the panel's head is as tall and as wide as the loaded one.
 */
export function IntakeGhost() {
  return (
    <div className={`${styles.tools} ${styles.ghost}`} aria-hidden="true">
      <span className={`${buttons.base} ${buttons.secondary}`}>Ablegen …</span>
      <span className={`${buttons.base} ${buttons.quiet}`}>Notiz</span>
      <span className={styles.toolField}>
        <span className={`label ${styles.toolLabel}`}>
          <span>Richtung</span>
        </span>
        <span className={`${fields.input} ${styles.toolSelect}`}>
          <span>Automatisch</span>
        </span>
      </span>
      <span className={styles.toolNote}>
        <span>{TRUST_LINE}</span>
      </span>
    </div>
  );
}

/**
 * Where the overlay's sentence stands, in px from the column's top: in the
 * middle of the part of the column the window shows (record fix round 2,
 * M-4). The column is often taller than the window — centred in all of it,
 * the sentence stood half below the fold at 1440 × 900. Kept 3 rem clear of
 * the column's own edges; `null` without a box to measure (the CSS centre).
 */
function visibleCentre(column: HTMLElement): number | null {
  const box = column.getBoundingClientRect();
  if (box.height <= 0) return null;
  const top = Math.max(box.top, 0);
  const bottom = Math.min(box.bottom, window.innerHeight);
  const clear = Math.min(48, box.height / 2);
  return Math.round(Math.min(Math.max((top + bottom) / 2 - box.top, clear), box.height - clear));
}

/**
 * The whole record column as the drop target (UX ruling 7, F-1), with an
 * overlay while a file hovers. `dragleave` counts only when the pointer
 * leaves the column for good — crossing onto one of its children fires it
 * too, and `DropArea` fixed exactly that flicker once already. The sentence
 * follows the window while the file hovers (`dragover` keeps coming, also
 * when the page scrolls under it).
 */
export function DropTarget({
  title,
  locked = false,
  busy,
  onFiles,
  onRefused,
  children,
}: {
  title: string;
  /**
   * The Vorgang is cancelled and takes no file (spec 2026-10-15 §5): the
   * overlay says so, and the drop is taken — a dropped file the page does not
   * catch is opened by the browser, which would leave the Werkbank — but
   * nothing is filed.
   */
  locked?: boolean;
  /** A drop is being filed — the overlay says so, and a drop now is refused aloud (T8). */
  busy: boolean;
  onFiles: (files: File[]) => void;
  onRefused: (files: File[]) => void;
  children: ReactNode;
}) {
  const [dragging, setDragging] = useState(false);
  const [textAt, setTextAt] = useState<number | null>(null);
  function hover(event: ReactDragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setDragging(true);
    setTextAt(visibleCentre(event.currentTarget));
  }
  return (
    <div
      data-testid="file-drop"
      className={styles.dropTarget}
      onDragEnter={hover}
      onDragOver={hover}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (locked) return;
        const files = Array.from(event.dataTransfer.files);
        if (busy) onRefused(files);
        else onFiles(files);
      }}
    >
      {children}
      {dragging ? (
        <div className={`${styles.dropOverlay} ${busy || locked ? styles.dropOverlayBusy : ""}`} aria-hidden="true">
          <p
            className={styles.dropOverlayText}
            style={textAt !== null ? { insetBlockStart: `${textAt}px` } : undefined}
          >
            {locked ? CANCELLED_DROP : busy ? STILL_FILING : `Loslassen legt in die Akte von „${title}“`}
            {busy || locked ? null : <span className={styles.dropOverlayNote}>{TRUST_LINE}</span>}
          </p>
        </div>
      ) : null}
    </div>
  );
}
